/**
 * ItineraryPageV2 — Midnight Explorer design, real data (Phase 2).
 *
 * Props same as original ItineraryPage:
 *   user, isAiWorking, setIsAiWorking, handleShowAiMessage
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { API_BASE_URL, API_PATH, BASE_PATH } from './config';
import TransportationFormModal from './TransportationFormModal';
import AddActivityModal from './AddActivityModal';
import TripMap from './TripMap';
import AIAssistant from './components/AIAssistant';
import ActivityDetailsModal from './components/ActivityDetailsModal';
import ShareTripModal from './components/ShareTripModal';
import CurrencyConverterModal from './components/CurrencyConverterModal';
import countriesCities from './data/countries_cities.json';
import dayjs from 'dayjs';
import { getActivityDisplayTitle } from './utils/activityUtils';
import InteractiveDailyActivityBuilder from './components/InteractiveDailyActivityBuilder';
import AIIcon from './components/AIIcon';
import WandIcon from './components/WandIcon';
import AiImportButton from './components/AiImportButton';
import AiItineraryImportModal from './components/AiItineraryImportModal';
import './components/ChatFlip.css';
import { getSnapshot, saveSnapshot, outboxCount } from './utils/offlineStore';
import { isOnline, flushOutbox, queueOrSend, onReconnect } from './utils/tripSync';
import {
  Plane, MapPin, Calendar, Clock, Plus, MoreVertical, Menu,
  ChevronLeft, Share2, Map, DollarSign, Camera, Bot,
  Hotel, Train, Car, Footprints, Ship, Bus, Loader2, AlertCircle,
  GripVertical, Edit2, Trash2, Lock, Pin, Info,
  ChevronDown, ChevronUp, ExternalLink,
  CloudOff, RefreshCw,
} from 'lucide-react';



// ─────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────

/** Build an Expedia hotel-search affiliate URL for a given city + date range */
const buildDayHotelUrl = (city, checkInDate, checkOutDate) => {
  if (!city || !checkInDate || !checkOutDate) return null;
  const params = new URLSearchParams({
    destination: city,
    flexibility: '0_DAY',
    d1: checkInDate,
    startDate: checkInDate,
    d2: checkOutDate,
    endDate: checkOutDate,
    adults: '2',
    rooms: '1',
    sort: 'RECOMMENDED',
  });
  const expediaUrl = `https://www.expedia.com/Hotel-Search?${params.toString()}`;
  return `https://www.jdoqocy.com/click-101602496-14580829?url=${encodeURIComponent(expediaUrl)}`;
};

const calcEndTime = (startTime, durationMin) => {
  if (!startTime || !durationMin) return null;
  const [h, m] = startTime.split(':').map(Number);
  const total = h * 60 + m + Number(durationMin);
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Flight connection (layover) detection — see memory: flight-activity-schema.
// A "connection" is two consecutive flights where the arrival airport of the
// first is the departure airport of the second, and the gap is under 12h (or
// both occur on the same calendar day).
// ---------------------------------------------------------------------------
const isFlightActivity = (activity) => {
  if (!activity) return false;
  return activity.type === 'flight' ||
    activity.subtype === 'flight' ||
    (activity.type === 'transportation' && activity.subtype === 'flight');
};

const getFlightEndpoints = (activity) => {
  if (!isFlightActivity(activity)) return null;
  let sd = activity.structured_data;
  if (typeof sd === 'string') { try { sd = JSON.parse(sd); } catch { sd = null; } }
  if (Array.isArray(sd)) sd = sd[0];
  sd = sd || {};

  // Pull each field from the most authoritative source available. A single
  // activity can carry several schemas at once (e.g. flat flight keys plus a
  // stale transportation_details block merged from the structured_data table),
  // so we prefer explicit flight keys / Amadeus details over legacy fields
  // rather than committing to one branch.
  const details = (sd.details && (sd.details.departure || sd.details.arrival)) ? sd.details : null;
  const td = sd.transportation_details || {};

  const originCode = sd.origin_airport_code || details?.departure?.iataCode || td.from || '';
  const destCode = sd.destination_airport_code || details?.arrival?.iataCode || td.to || '';
  const originCity = sd.origin_city || activity.location || '';
  const destCity = sd.destination_city || activity.destination || '';
  const departDT = sd.start_date_time || details?.departure?.at || td.departure_time || null;
  const arriveDT = sd.end_date_time || details?.arrival?.at || td.arrival_time || null;

  return {
    originCode: String(originCode).trim(),
    destCode: String(destCode).trim(),
    originCity: String(originCity).trim(),
    destCity: String(destCity).trim(),
    departDT: departDT || null,
    arriveDT: arriveDT || null,
  };
};

// Parse a flight datetime as its wall-clock LOCAL time, ignoring any timezone
// designator/offset. A connection is always at one physical airport (flight 1's
// arrival airport == flight 2's departure airport), so both endpoints share a
// timezone and the naive wall-clock difference IS the true local layover — this
// is immune to the browser/runtime timezone and to mixed offset encodings.
// Backed by Date.UTC so getUTC*/getTime() reflect the raw wall-clock values.
const parseFlightDateTime = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi));
};

const isSameWallClockDay = (a, b) =>
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate();

const formatLayoverDuration = (ms) => {
  const totalMinutes = Math.round(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

const getFlightConnection = (prev, next) => {
  const a = getFlightEndpoints(prev);
  const b = getFlightEndpoints(next);
  if (!a || !b) return null;

  const arrCode = a.destCode.toUpperCase();
  const depCode = b.originCode.toUpperCase();
  const arrCity = a.destCity.toLowerCase();
  const depCity = b.originCity.toLowerCase();
  const sameByCode = arrCode && depCode && arrCode === depCode;
  const sameByCity = arrCity && depCity && arrCity === depCity;
  if (!sameByCode && !sameByCity) return null;

  const arriveDT = parseFlightDateTime(a.arriveDT);
  const departDT = parseFlightDateTime(b.departDT);

  let layoverText = '';
  let qualifies = false;
  if (arriveDT && departDT) {
    const gapMs = departDT.getTime() - arriveDT.getTime();
    if (gapMs < 0) return null; // second flight departs before first arrives
    const sameDay = isSameWallClockDay(arriveDT, departDT);
    const withinWindow = gapMs < 12 * 60 * 60 * 1000;
    qualifies = withinWindow || sameDay;
    layoverText = formatLayoverDuration(gapMs);
  } else {
    // No usable times: fall back to "same day" (already grouped per-day here).
    qualifies = true;
  }
  if (!qualifies) return null;

  return {
    code: arrCode || depCode,
    city: a.destCity || b.originCity || '',
    layoverText,
  };
};

const TRANSPORT_ICON = {
  flight: Plane,
  train: Train,
  car: Car,
  walk: Footprints,
};

// Maps a transportation subtype to its icon + label (used for the per-day transport badge)
const TRANSPORT_MODE = {
  flight: { Icon: Plane, label: 'Flight' },
  plane: { Icon: Plane, label: 'Flight' },
  train: { Icon: Train, label: 'Train' },
  bus: { Icon: Bus, label: 'Bus' },
  boat_ferry: { Icon: Ship, label: 'Boat / ferry' },
  boat: { Icon: Ship, label: 'Boat' },
  ferry: { Icon: Ship, label: 'Ferry' },
  cruise: { Icon: Ship, label: 'Cruise' },
  car: { Icon: Car, label: 'Car / taxi' },
  taxi: { Icon: Car, label: 'Taxi' },
  walk: { Icon: Footprints, label: 'Walk' },
  other: { Icon: Car, label: 'Transport' },
};

const transportModeFor = (activity) => {
  if (activity.type === 'flight') return TRANSPORT_MODE.flight;
  if (activity.type !== 'transportation') return null;
  return TRANSPORT_MODE[activity.subtype] || TRANSPORT_MODE.other;
};

const activityIcon = (activity) => {
  if (activity.type === 'flight') return Plane;
  if (activity.type === 'lodging') return Hotel;
  if (activity.type === 'transportation') return TRANSPORT_ICON[activity.subtype] || Car;
  return MapPin;
};

const activityColor = (activity) => {
  if (activity.type === 'flight') return 'tw-text-sky-400';
  if (activity.type === 'lodging') return 'tw-text-emerald-400';
  if (activity.type === 'transportation') return 'tw-text-violet-400';
  return 'tw-text-accent-glow';
};

// ─────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────

// Fix image URLs that use the old /travel/ path instead of /travel2/
const fixImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/travel/')) return '/travel2/' + url.slice('/travel/'.length);
  return url;
};

// Route external image URLs through our same-origin proxy so hotlinked/scrape
// (e.g. Bing) thumbnails actually render. Local paths pass through unchanged.
const proxify = (url) => {
  if (!url || !/^https?:\/\//i.test(url)) return url;
  try {
    const b64 = btoa(url).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `${API_PATH}/image_proxy.php?u=${b64}`;
  } catch (e) {
    return url;
  }
};

const getViatorPriceTier = (price) => {
  if (!price) return null;
  const p = Number(price);
  if (p < 30) return '$';
  if (p < 80) return '$$';
  return '$$$';
};

/** Single activity row */
const ActivityRow = ({ activity, onEdit, onDelete, onPin, onShowDetails, nextActivity, previousActivity, dragHandleProps, isDragging, dayCity }) => {
  const Icon = activityIcon(activity);
  const colorClass = activityColor(activity);
  // For lodging: never compute a duration-based end time — show check-out time from structured_data instead
  let endTime = null;
  if (activity.type === 'lodging') {
    let sd = activity.structured_data;
    if (typeof sd === 'string') { try { sd = JSON.parse(sd); } catch { sd = null; } }
    if (sd?.check_out_date_time) endTime = sd.check_out_date_time.split(' ')[1]?.slice(0, 5) || null;
  } else {
    endTime = (activity.end_time ? activity.end_time.slice(0, 5) : null)
      || calcEndTime(activity.start_time, activity.duration);
  }
  const viatorPriceTier = getViatorPriceTier(activity.viator_price);

  const isSpecialType = (
    activity.type === 'flight' ||
    activity.type === 'lodging' ||
    activity.type === 'Lodging' ||
    activity.type === 'transportation' ||
    Number(activity.type) === 1 ||
    activity.title?.startsWith('Travel from')
  );

  // Dropdown menu state (regular activities only)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target) && !menuBtnRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    if (menuBtnRef.current) {
      const rect = menuBtnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX - 130 });
    }
    setMenuOpen(v => !v);
  };

  // Thumbnail
  const [thumbnailUrl, setThumbnailUrl] = useState(fixImageUrl(activity.preview_image_url) || null);
  const thumbnailFetchRef = useRef(false);
  const isRegularActivity = !isSpecialType;
  useEffect(() => {
    if (!thumbnailUrl && !thumbnailFetchRef.current && isRegularActivity) {
      thumbnailFetchRef.current = true;
      const token = localStorage.getItem('token');
      const parts = (activity.location || '').split(',').map(p => p.trim());
      fetch(`${API_PATH}/trips/get_activity_preview_image.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          activity_name: activity.title,
          city: parts[0] || '',
          country: parts.length > 1 ? parts[parts.length - 1] : '',
          activity_id: activity.activity_id
        })
      })
        .then(r => r.json())
        .then(data => { if (data.success && data.image_url) setThumbnailUrl(fixImageUrl(data.image_url)); })
        .catch(() => {});
    }
  }, [activity.activity_id]); // eslint-disable-line

  // Overlap detection. Hotel check-in/check-out are all-day activities without a
  // hard-set time, so they never participate in conflict detection — neither on the
  // lodging row itself, nor against an adjacent activity.
  const isLodging = (act) =>
    act && (act.type === 'lodging' || act.type === 'Lodging');
  let overlap = false;
  if (!isLodging(activity) && !isLodging(nextActivity) && endTime && nextActivity?.start_time) {
    const endH = endTime.replace(':', '');
    const nextH = nextActivity.start_time.slice(0, 5).replace(':', '');
    overlap = endH > nextH;
  }
  let previousOverlap = false;
  if (!isLodging(activity) && !isLodging(previousActivity) && previousActivity?.start_time) {
    const prevEnd = (previousActivity.end_time ? previousActivity.end_time.slice(0, 5) : null)
      || calcEndTime(previousActivity.start_time, previousActivity.duration);
    if (prevEnd && activity.start_time) {
      const prevEndH = prevEnd.replace(':', '');
      const curStartH = activity.start_time.slice(0, 5).replace(':', '');
      previousOverlap = prevEndH > curStartH;
    }
  }
  const hasConflict = overlap || previousOverlap;

  // City mismatch: activity location doesn't contain the day's city
  const isSpecialForMismatch = activity.type === 'flight' || activity.type === 'transportation';
  const cityMismatch =
    !isSpecialForMismatch &&
    dayCity &&
    activity.location &&
    String(activity.location) !== '0' &&
    !activity.location.toLowerCase().includes(dayCity.toLowerCase());

  const handleRowClick = (e) => {
    if (e.target.closest('button')) return;
    onShowDetails && onShowDetails(activity);
  };

  return (
    <div
      onClick={handleRowClick}
      className={`tw-relative tw-group tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2.5 tw-rounded-xl hover:tw-bg-surface-3/60 tw-transition-all tw-duration-150 tw-cursor-pointer ${
        isDragging ? 'tw-bg-surface-3/80 tw-shadow-lg' : ''
      } ${hasConflict ? 'tw-border-l-2 tw-border-amber-500/60' : cityMismatch ? 'tw-border-l-2 tw-border-orange-500/70' : ''}`}
    >
      {/* Drag handle */}
      <span
        aria-label="Drag to reorder"
        className="tw-flex tw-items-center tw-self-center tw-opacity-50 tw-group-hover:tw-opacity-90 tw-transition-opacity tw-text-slate-400 tw-cursor-grab tw-active:tw-cursor-grabbing tw-touch-none tw-flex-shrink-0"
        {...(dragHandleProps || {})}
      >
        <GripVertical size={18} />
      </span>

      {/* Left cluster: time + icon */}
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
        <div className="tw-w-10 tw-text-right tw-flex-shrink-0">
          <span className={`tw-text-sm tw-font-semibold tw-tabular-nums ${hasConflict ? 'tw-text-amber-400' : 'tw-text-muted-fore'}`}>
            {activity.start_time ? activity.start_time.slice(0, 5) : '--:--'}
          </span>
          {endTime && (
            <span className="tw-block tw-text-xs tw-text-muted tw-tabular-nums">{endTime}</span>
          )}
        </div>
        <span className={`tw-flex-shrink-0 tw-p-1.5 tw-rounded-lg tw-bg-surface-3 ${colorClass}`}>
          <Icon size={14} aria-hidden="true" />
        </span>
      </div>

      {/* Text — takes all remaining space */}
      <div className="tw-flex-1 tw-min-w-0">
        <p className="tw-text-sm tw-font-medium tw-text-muted-fore tw-leading-snug tw-truncate">
          {activity.title}
          {!!activity.is_pinned && (
            <Pin size={11} className="tw-inline tw-ml-1.5 tw-text-amber-400 tw-relative -tw-top-px" aria-label="Pinned" />
          )}
        </p>
        {activity.location && String(activity.location) !== '0' && (
          <p className="tw-text-xs tw-text-muted tw-mt-0.5 tw-truncate">
            <MapPin size={10} className="tw-inline tw-mr-0.5" aria-hidden="true" />
            {activity.location}
          </p>
        )}
        {hasConflict && (
          <p className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-amber-400 tw-mt-0.5">
            <AlertCircle size={10} className="tw-flex-shrink-0" />
            {previousOverlap ? 'Overlaps with previous' : 'Overlaps with next'}
          </p>
        )}
        {cityMismatch && (
          <p className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-orange-400 tw-mt-0.5" title={`Activity is in a different city than the day city (${dayCity})`}>
            <AlertCircle size={10} className="tw-flex-shrink-0" />
            Not in {dayCity}
          </p>
        )}
      </div>

      {/* Price tier — fixed width column */}
      <div className="tw-flex-shrink-0 tw-w-8 tw-flex tw-items-center tw-justify-center">
        {viatorPriceTier && !isSpecialType && (
          <span className="tw-text-xs tw-font-bold tw-text-emerald-400 tw-bg-emerald-500/10 tw-px-1.5 tw-py-0.5 tw-rounded-md tw-tabular-nums tw-leading-none">
            {viatorPriceTier}
          </span>
        )}
      </div>

      {/* Thumbnail — fixed width column */}
      <div className="tw-flex-shrink-0 tw-w-12 sm:tw-w-14 tw-flex tw-items-center tw-justify-center">
        {thumbnailUrl && isRegularActivity ? (
          <img
            src={proxify(thumbnailUrl)}
            alt={activity.title}
            onError={(e) => { e.target.style.display = 'none'; }}
            className="tw-w-12 tw-h-12 sm:tw-w-14 sm:tw-h-14 tw-object-cover tw-rounded-xl tw-border tw-border-white/10"
          />
        ) : null}
      </div>

    </div>
  );
};

/** Day card */
const DayCard = ({ day, activities = [], connectionByActivityId = {}, hotelReservation = null, onAdd, onAiSuggest, onSuggestActivities, onEditActivity, onDeleteActivity, onPinActivity, onShowDetails, onUpdateCity }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [editingCity, setEditingCity] = useState(false);
  const [cityInput, setCityInput] = useState(day.city || '');
  const cityInputRef = useRef(null);

  // Actions burger menu
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsPos, setActionsPos] = useState({ top: 0, left: 0 });
  const actionsBtnRef = useRef(null);
  const actionsMenuRef = useRef(null);
  useEffect(() => {
    if (!actionsOpen) return;
    const close = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target) && !actionsBtnRef.current?.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [actionsOpen]);
  const toggleActionsMenu = (e) => {
    e.stopPropagation();
    if (actionsBtnRef.current) {
      const rect = actionsBtnRef.current.getBoundingClientRect();
      setActionsPos({ top: rect.bottom + window.scrollY + 6, left: rect.right + window.scrollX - 200 });
    }
    setActionsOpen(v => !v);
  };

  const startEditCity = (e) => {
    e.stopPropagation();
    setCityInput(day.city || '');
    setEditingCity(true);
    setTimeout(() => cityInputRef.current?.focus(), 0);
  };

  const commitCity = () => {
    setEditingCity(false);
    const trimmed = cityInput.trim();
    if (trimmed !== (day.city || '')) {
      onUpdateCity && onUpdateCity(day.dayNumber, trimmed);
    }
  };

  const handleCityKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitCity(); }
    if (e.key === 'Escape') { setEditingCity(false); setCityInput(day.city || ''); }
  };

  const dateLabel = day.date && !day.date.startsWith('day-')
    ? new Date(day.date + 'T00:00').toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : '';

  // Distinct transportation modes for this day (deduped by icon), shown as green badges
  const transportModes = (() => {
    const seen = new Set();
    const modes = [];
    for (const a of activities) {
      const mode = transportModeFor(a);
      if (mode && !seen.has(mode.Icon)) {
        seen.add(mode.Icon);
        modes.push(mode);
      }
    }
    return modes;
  })();

  return (
    <section
      aria-label={`${day.label}${dateLabel ? ' — ' + dateLabel : ''}`}
      className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-transition-all tw-duration-200"
    >
      {/* Day header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-surface-border">
        <button
          onClick={() => setCollapsed(c => !c)}
          className="tw-flex tw-items-center tw-gap-3 tw-text-left tw-group"
          aria-expanded={!collapsed}
        >
          <span className="tw-flex-1 tw-min-w-0">
            {dateLabel && (
              <span className="tw-block tw-text-sm tw-font-bold tw-text-muted-fore tw-leading-snug">{dateLabel}</span>
            )}
            <span className={`tw-leading-snug tw-truncate tw-block ${dateLabel ? 'tw-text-xs tw-font-normal tw-text-muted' : 'tw-text-sm tw-font-bold tw-text-muted-fore'}`}>
              {day.label}
              {editingCity ? (
                <span
                  className="tw-inline-flex tw-items-center tw-ml-2"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    ref={cityInputRef}
                    value={cityInput}
                    onChange={e => setCityInput(e.target.value)}
                    onBlur={commitCity}
                    onKeyDown={handleCityKeyDown}
                    placeholder="City name"
                    className="tw-text-xs tw-font-normal tw-bg-surface-3 tw-border tw-border-accent/50 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-muted-fore tw-outline-none tw-w-28"
                  />
                </span>
              ) : (
                <span
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-ml-2 tw-group/city"
                  onClick={e => e.stopPropagation()}
                >
                  <span className="tw-text-xs tw-font-normal tw-text-muted-fore/70">
                    {day.city || <span className="tw-italic tw-text-muted/50">no city</span>}
                  </span>
                  <button
                    type="button"
                    onClick={startEditCity}
                    aria-label="Edit city name"
                    title="Edit city"
                    className="tw-opacity-0 tw-group-hover/city:tw-opacity-100 tw-transition-opacity tw-text-muted tw-hover:tw-text-accent tw-p-0.5 tw-rounded"
                  >
                    <Edit2 size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
            </span>
          </span>
          <span className="tw-text-muted tw-group-hover:tw-text-muted-fore tw-transition-colors">
            {collapsed
              ? <ChevronDown size={14} aria-hidden="true" />
              : <ChevronUp size={14} aria-hidden="true" />
            }
          </span>
        </button>

        {/* Day actions */}
        <div className="tw-flex tw-items-center tw-gap-1">
          {/* Transportation badges — green icon per mode on this day */}
          {transportModes.map(({ Icon, label }) => (
            <span
              key={label}
              role="img"
              aria-label={`${label} on ${day.label}`}
              title={label}
              className="tw-p-1.5 tw-rounded-xl tw-inline-flex tw-items-center tw-text-emerald-500"
            >
              <Icon size={15} aria-hidden="true" />
            </span>
          ))}

          {/* Hotel status — always visible */}
          <button
            type="button"
            disabled={!hotelReservation}
            onClick={() => hotelReservation && onShowDetails && onShowDetails(hotelReservation)}
            aria-label={hotelReservation ? `View hotel reservation for ${day.label}` : `No hotel reservation for ${day.label}`}
            title={hotelReservation ? 'View hotel reservation for this night' : 'No hotel reservation for this night'}
            className={`tw-p-1.5 tw-rounded-xl tw-inline-flex tw-items-center tw-transition-all tw-duration-150 ${hotelReservation ? 'tw-text-emerald-500 tw-hover:tw-bg-emerald-500/10 tw-cursor-pointer' : 'tw-text-red-500 tw-cursor-default'}`}
          >
            <Hotel size={15} aria-hidden="true" />
          </button>

          {/* Burger menu — everything else */}
          <button
            ref={actionsBtnRef}
            type="button"
            onClick={toggleActionsMenu}
            aria-label={`Day actions for ${day.label}`}
            aria-haspopup="true"
            aria-expanded={actionsOpen}
            title="Day actions"
            className={`tw-p-1.5 tw-rounded-xl tw-transition-all tw-duration-150 ${actionsOpen ? 'tw-text-accent-glow tw-bg-accent/10' : 'tw-text-muted tw-hover:tw-text-accent-glow tw-hover:tw-bg-accent/10'}`}
          >
            <Menu size={15} aria-hidden="true" />
          </button>

          {actionsOpen && ReactDOM.createPortal(
            <div
              ref={actionsMenuRef}
              role="menu"
              style={{ position: 'absolute', top: actionsPos.top, left: actionsPos.left, zIndex: 1000 }}
              className="tw-w-52 tw-py-1 tw-rounded-xl tw-bg-surface-2 tw-border tw-border-surface-border tw-shadow-xl tw-backdrop-blur-md"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => { setActionsOpen(false); onAdd && onAdd(day.dayNumber); }}
                className="tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-sm tw-text-muted-fore tw-hover:tw-bg-accent/10 tw-hover:tw-text-accent-glow tw-transition-colors"
              >
                <Plus size={15} className="tw-text-accent-glow" aria-hidden="true" />
                Add Activity
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setActionsOpen(false); onAiSuggest && onAiSuggest(day); }}
                className="tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-sm tw-text-muted-fore tw-hover:tw-bg-violet-500/10 tw-hover:tw-text-violet-400 tw-transition-colors"
              >
                <AIIcon sx={{ fontSize: 15 }} className="tw-text-violet-400" />
                AI Chat
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setActionsOpen(false); onSuggestActivities && onSuggestActivities(day); }}
                className="tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-sm tw-text-muted-fore tw-hover:tw-bg-emerald-500/10 tw-hover:tw-text-emerald-400 tw-transition-colors"
              >
                <WandIcon sx={{ fontSize: 15 }} className="tw-text-emerald-400" />
                Suggest Places
              </button>
              {day.city && day.date && !day.date.startsWith('day-') && (() => {
                const nextDate = new Date(day.date + 'T00:00');
                nextDate.setDate(nextDate.getDate() + 1);
                const checkOut = nextDate.toISOString().slice(0, 10);
                const hotelUrl = buildDayHotelUrl(day.city, day.date, checkOut);
                return hotelUrl ? (
                  <a
                    href={hotelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="menuitem"
                    onClick={() => setActionsOpen(false)}
                    aria-label={`Search hotels on Expedia in ${day.city} for ${day.label}`}
                    title={`Search hotels on Expedia in ${day.city}`}
                    className="tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-sm tw-text-muted-fore tw-hover:tw-bg-sky-500/10 tw-hover:tw-text-sky-400 tw-transition-colors"
                  >
                    <Hotel size={15} className="tw-text-sky-400" aria-hidden="true" />
                    <span className="tw-flex-1 tw-text-left">Find Hotel</span>
                    <ExternalLink size={12} strokeWidth={2.5} aria-hidden="true" />
                  </a>
                ) : null;
              })()}
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Activity list */}
      {!collapsed && (
        <div className="tw-py-1">
          {activities.length === 0 ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-8 tw-text-center">
              <span className="tw-p-3 tw-rounded-full tw-bg-accent/5">
                <Calendar size={22} className="tw-text-muted" aria-hidden="true" />
              </span>
              <p className="tw-text-xs tw-text-muted">No activities yet</p>
              <button
                onClick={() => onAdd && onAdd(day.dayNumber)}
                aria-label={`Add activity to ${day.label}`}
                className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-accent tw-hover:tw-text-accent-glow tw-transition-colors"
              >
                <Plus size={13} aria-hidden="true" />
                Add activity
              </button>
            </div>
          ) : (
            <Droppable droppableId={`day-${day.dayNumber}`}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {activities.map((act, idx) => (
                    <Draggable key={String(act.activity_id)} draggableId={`activity-${act.activity_id}`} index={idx} isDragDisabled={!!act.is_pinned}>
                      {(dragProvided, dragSnapshot) => {
                        // Connecting flight: the previous flight in the trip
                        // (possibly on an earlier day) arrives at this flight's
                        // departure airport, within 12h / same day. Precomputed
                        // across the whole trip so cross-day layovers are caught.
                        const connection = connectionByActivityId[act.activity_id] || null;
                        const dragChild = (
                          <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                            {connection && (
                              <div className="tw-flex tw-items-center tw-gap-2 tw-mx-3 tw-my-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-border-l-[3px] tw-border-dashed tw-border-sky-400 tw-bg-sky-400/5">
                                <Plane size={15} className="tw-text-sky-400 tw-rotate-90" aria-hidden="true" />
                                <span className="tw-text-xs tw-font-semibold tw-text-sky-300">
                                  Connection{connection.city ? ` — ${connection.city}` : ''}{connection.code ? ` (${connection.code})` : ''}
                                </span>
                                {connection.layoverText && (
                                  <span className="tw-text-[11px] tw-text-muted">· Layover {connection.layoverText}</span>
                                )}
                              </div>
                            )}
                            <ActivityRow
                              activity={act}
                              nextActivity={activities[idx + 1] || null}
                              previousActivity={activities[idx - 1] || null}
                              onEdit={onEditActivity}
                              onDelete={onDeleteActivity}
                              onPin={onPinActivity}
                              onShowDetails={onShowDetails}
                              dragHandleProps={dragProvided.dragHandleProps}
                              isDragging={dragSnapshot.isDragging}
                              dayCity={editingCity ? cityInput : (day.city || '')}
                            />
                          </div>
                        );
                        if (dragSnapshot.isDragging) {
                          return ReactDOM.createPortal(dragChild, document.body);
                        }
                        return dragChild;
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </div>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────

const ItineraryPageV2 = ({ user, isAiWorking, setIsAiWorking, handleShowAiMessage }) => {
  const { t, i18n } = useTranslation();
  const { tripId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ── State (mirrors ItineraryPage.jsx exactly) ─────────
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activitiesByDay, setActivitiesByDay] = useState({});
  const [computedTripDays, setComputedTripDays] = useState([]);
  const [lockedDays, setLockedDays] = useState({});
  const [showTransportationModal, setShowTransportationModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [editActivity, setEditActivity] = useState(null);
  const [selectedDayForTransport, setSelectedDayForTransport] = useState(null);
  const [transportDefaultDate, setTransportDefaultDate] = useState('');
  const [transportDefaultDestination, setTransportDefaultDestination] = useState('');
  const [selectedTransportationRecord, setSelectedTransportationRecord] = useState(null);
  const [transportationDetailsOpen, setTransportationDetailsOpen] = useState(false);
  const [editingTransportationRecord, setEditingTransportationRecord] = useState(null);
  const [dailyTransportation, setDailyTransportation] = useState({});
  const [activityMenuAnchorEl, setActivityMenuAnchorEl] = useState(null);
  const initialGenerationStarted = useRef(false);
  const generationTriggeredFromEdit = useRef(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showFlexibleTripMessage, setShowFlexibleTripMessage] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [aiContext, setAiContext] = useState({ type: 'general', id: null, activity: null });
  const [showMap, setShowMap] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAiImport, setShowAiImport] = useState(false);
  const [showCurrencyConverter, setShowCurrencyConverter] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [hotelStays, setHotelStays] = useState([]);
  const [cityContexts, setCityContexts] = useState({});
  const [viatorFetchedIds, setViatorFetchedIds] = useState(new Set());
  const [showInteractiveBuilder, setShowInteractiveBuilder] = useState(false);
  const [suggestActivitiesForDay, setSuggestActivitiesForDay] = useState(null);
  const [linkedReports, setLinkedReports] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempDuration, setTempDuration] = useState('');
  // On-load flight change / verification notices from check_flight_changes.php
  const [flightNotices, setFlightNotices] = useState([]);
  // Offline-first sync state (drives the header status chip).
  const [online, setOnline] = useState(isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const refreshPending = useCallback(async () => {
    try { setPendingCount(await outboxCount(tripId)); } catch (_) {}
  }, [tripId]);
  const flightCheckRunRef = useRef(false);

  // tripDays alias (same as original render section)
  const tripDays = computedTripDays;

  const isTripFlexible = !trip?.start_date || trip?.start_date === '0000-00-00';

  const handleOpenDatePicker = () => {
    setTempDuration(trip?.duration_days || 5);
    setTempStartDate('');
    setShowDatePicker(true);
  };

  const handleCloseDatePicker = () => {
    setShowDatePicker(false);
    setTempStartDate('');
    setTempDuration('');
  };

  const handleSaveTripDates = async () => {
    if (!tempStartDate || !tempDuration) {
      alert(t('select_date_duration_error'));
      return;
    }
    try {
      const oldDuration = trip?.duration_days || 0;
      const newDuration = parseInt(tempDuration);
      const newEnd = dayjs(tempStartDate).add(Math.max(0, newDuration - 1), 'day').format('YYYY-MM-DD');

      // Optimistic: apply new dates locally + persist, then rebuild the day layout
      // from the merged snapshot (keeps existing activities intact).
      const updatedTrip = { ...(trip || {}), start_date: tempStartDate, end_date: newEnd, duration_days: newDuration };
      setTrip(updatedTrip);
      await saveSnapshot(tripId, { trip: updatedTrip });
      const merged = await getSnapshot(tripId);
      if (merged) applySnapshot(merged);
      handleCloseDatePicker();

      const res = await queueOrSend({
        tripId, method: 'POST',
        url: `${API_PATH}/trips/update_trip_dates.php`,
        body: { trip_id: tripId, start_date: tempStartDate, duration_days: newDuration },
      });
      await refreshPending();
      if (res.sent) {
        await fetchTripData({ background: true });
        // Regenerating the itinerary for the new days is an online-only side effect.
        if (newDuration > oldDuration && isOnline()) {
          setTimeout(() => { generateItinerary(); }, 500);
        }
      }
    } catch (error) {
      console.error('Error updating trip dates:', error);
      alert(t('update_dates_error'));
    }
  };

  // ── Offline-first data layer ───────────────────────────
  // applySnapshot turns a RAW trip image ({ trip, activities, locked, lodging,
  // cityContexts }) into rendered React state. It's the single place that holds
  // the grouping/normalization logic, shared by the cached load and the live fetch.
  const applySnapshot = useCallback((raw) => {
    const tripData = raw && raw.trip;
    if (!tripData) return;
    setTrip(tripData);

    const days = [];
    const routeMap = {};
    if (Array.isArray(tripData.route)) {
      tripData.route.forEach(r => { routeMap[r.day] = r; });
    }
    if (tripData.start_date && tripData.end_date) {
      const start = new Date(tripData.start_date);
      const end = new Date(tripData.end_date);
      let cur = new Date(start);
      let n = 1;
      while (cur <= end) {
        const r = routeMap[n] || {};
        days.push({ label: `Day ${n}`, date: cur.toISOString().split('T')[0], dayNumber: n, city: r.city || '', country: r.country || '' });
        cur.setDate(cur.getDate() + 1);
        n++;
      }
    } else if (tripData.duration_days) {
      for (let i = 1; i <= tripData.duration_days; i++) {
        const r = routeMap[i] || {};
        days.push({ label: `Day ${i}`, date: `day-${i}`, dayNumber: i, city: r.city || '', country: r.country || '' });
      }
    }

    const allActivities = Array.isArray(raw.activities) ? raw.activities : [];

    const dayNumberToDate = {};
    days.forEach(d => { dayNumberToDate[d.dayNumber] = d.date; });

    if (days.length === 0) {
      const numericDays = allActivities.map(a => Number(a.day)).filter(n => !isNaN(n) && n > 0);
      if (numericDays.length > 0) {
        const maxDay = Math.max(...numericDays);
        for (let i = 1; i <= maxDay; i++) days.push({ label: `Day ${i}`, date: `day-${i}`, dayNumber: i });
        days.forEach(d => { dayNumberToDate[d.dayNumber] = d.date; });
      }
    }
    setComputedTripDays(days);

    const normalizeToISODate = (val) => {
      if (!val && val !== 0) return null;
      if (val instanceof Date && !isNaN(val.getTime())) return val.toISOString().split('T')[0];
      if (typeof val === 'number') return null;
      if (typeof val === 'string') {
        const s = val.trim();
        const d1 = new Date(s);
        if (!isNaN(d1.getTime())) return d1.toISOString().split('T')[0];
        const dm1 = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
        if (dm1) {
          const d2 = new Date(parseInt(dm1[3], 10), parseInt(dm1[2], 10) - 1, parseInt(dm1[1], 10));
          if (!isNaN(d2.getTime())) return d2.toISOString().split('T')[0];
        }
        const ymd = s.match(/^(\d{4})[\-]?(\d{2})[\-]?(\d{2})$/);
        if (ymd) {
          const d3 = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
          if (!isNaN(d3.getTime())) return d3.toISOString().split('T')[0];
        }
      }
      return null;
    };

    const grouped = {};
    const unmapped = [];
    allActivities.forEach(activity => {
      let dateKey = null;
      if (activity.day_date) {
        const norm = normalizeToISODate(activity.day_date);
        if (norm && days.find(d => d.date === norm)) dateKey = norm;
      }
      if (!dateKey && activity.day !== undefined && activity.day !== null) {
        const numericDay = Number(activity.day);
        if (!isNaN(numericDay) && dayNumberToDate[numericDay]) dateKey = dayNumberToDate[numericDay];
      }
      if (!dateKey && typeof activity.day === 'string') {
        const norm2 = normalizeToISODate(activity.day);
        if (norm2 && days.find(d => d.date === norm2)) dateKey = norm2;
      }
      if (!dateKey && typeof activity.day === 'string') {
        const matchByLabel = days.find(d => d.label === activity.day);
        if (matchByLabel) dateKey = matchByLabel.date;
      }
      if (dateKey) {
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(activity);
      } else {
        unmapped.push(activity);
      }
    });
    if (unmapped.length > 0) grouped['unscheduled'] = unmapped;
    setActivitiesByDay(grouped);

    if (Array.isArray(raw.locked)) {
      const map = {};
      raw.locked.forEach(d => { map[d.day_date] = d.is_locked; });
      setLockedDays(map);
    }

    const lodgingData = raw.lodging;
    if (lodgingData && lodgingData.success && Array.isArray(lodgingData.lodgings)) {
      setHotelStays(lodgingData.lodgings.map(l => ({
        id: l.id, dayNumber: l.dayNumber,
        dayLabel: days.find(d => d.dayNumber === l.dayNumber)?.label || `Day ${l.dayNumber}`,
        checkInDateTime: `${l.checkInDate}T${l.checkInTime?.substring(0, 5) || '15:00'}`,
        checkOutDateTime: `${l.checkOutDate}T${l.checkOutTime?.substring(0, 5) || '11:00'}`,
        hotelName: l.hotelName, hotelAddress: l.hotelAddress,
        hotelCity: l.hotelCity, hotelCountry: l.hotelCountry,
        reference: l.reference, notes: l.notes, updatedAt: l.updatedAt,
      })));
    }

    if (raw.cityContexts && typeof raw.cityContexts === 'object') {
      setCityContexts(raw.cityContexts);
    }
  }, []);

  // Live fetch: pull the 4 core resources, persist them as this trip's offline
  // image, then apply. Pass { background: true } to refresh silently without the
  // full-screen spinner (used after an instant cached paint / on reconnect).
  const fetchTripData = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { setError(t('auth_token_missing')); return; }

      const tripResponse = await fetch(`${API_PATH}/trips/get_single.php?trip_id=${tripId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const tripData = await tripResponse.json();
      if (!tripResponse.ok) {
        if (tripResponse.status === 404) { navigate('/'); return; }
        setError(tripData.message || t('fetch_trip_details_error'));
        return;
      }

      const activitiesResponse = await fetch(`${API_PATH}/activities.php?trip_id=${tripId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const allActivities = await activitiesResponse.json();
      if (!activitiesResponse.ok) { setError(allActivities.message || t('fetch_activities_error')); return; }

      let lockedData = [];
      try {
        const lockedRes = await fetch(`${API_PATH}/days/get_locked.php?trip_id=${tripId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const parsed = await lockedRes.json();
        if (lockedRes.ok && Array.isArray(parsed)) lockedData = parsed;
      } catch (_) {}

      let lodgingData = null;
      try {
        const lodgingRes = await fetch(`${API_PATH}/lodging.php?trip_id=${tripId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const parsed = await lodgingRes.json();
        if (lodgingRes.ok && parsed && parsed.success) lodgingData = parsed;
      } catch (_) {}

      const raw = { trip: tripData, activities: allActivities, locked: lockedData, lodging: lodgingData };
      await saveSnapshot(tripId, raw);
      applySnapshot(raw);
      setDailyTransportation({});
    } catch (err) {
      console.error('Error fetching trip data:', err);
      // Don't clobber an already-rendered cached view on a background refresh failure.
      if (!background) setError(t('fetch_trip_data_error'));
    } finally {
      if (!background) setLoading(false);
    }
  }, [tripId, t, navigate, applySnapshot]);

  const fetchTransportationData = useCallback(async () => {
    const dayLabels = (computedTripDays || [])
      .map(d => d?.date)
      .filter(date => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date));
    if (dayLabels.length === 0) return;
    const token = localStorage.getItem('token');
    const fetched = {};
    for (const label of dayLabels) {
      try {
        const res = await fetch(`${API_BASE_URL}/get_transportation.php?trip_id=${tripId}&day_label=${encodeURIComponent(label)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) fetched[label] = await res.json();
      } catch (_) {}
    }
    setDailyTransportation(fetched);
  }, [tripId, computedTripDays]);

  const fetchCityContexts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !tripId) return;
      const res = await fetch(`${API_PATH}/trips/get_city_contexts.php?trip_id=${tripId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && typeof data === 'object') {
          setCityContexts(data);
          saveSnapshot(tripId, { cityContexts: data });
        }
      }
    } catch (_) {}
  }, [tripId]);

  const generateItinerary = useCallback(async () => {
    if (!trip) return;
    handleShowAiMessage(t('starting_generation'));
    setIsAiWorking(true);
    const token = localStorage.getItem('token');
    try {
      const jobRes = await fetch(`${API_PATH}/trips/start_generation_job.php?trip_id=${tripId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ trip_id: tripId })
      });
      if (!jobRes.ok) { const e = await jobRes.json(); setError(e.error || t('start_generation_error')); setIsAiWorking(false); return; }
      const { job_id: jobId } = await jobRes.json();
      handleShowAiMessage(t('generation_in_progress'));
      const maxPollTime = 10 * 60 * 1000;
      const startTime = Date.now();
      let pollCount = 0;
      const pollJobStatus = setInterval(async () => {
        pollCount++;
        if (Date.now() - startTime > maxPollTime) { clearInterval(pollJobStatus); setError(t('generation_timeout')); setIsAiWorking(false); return; }
        try {
          const statusRes = await fetch(`${API_PATH}/trips/check_job_status.php?job_id=${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!statusRes.ok) { clearInterval(pollJobStatus); setError(t('generation_lost_connection')); setIsAiWorking(false); return; }
          const { job } = await statusRes.json();
          if (job.current_step) handleShowAiMessage(`${job.current_step}... (${job.progress || 0}%)`);
          if (job.status === 'processing' && pollCount % 3 === 0) fetchTripData();
          if (job.status === 'completed') {
            clearInterval(pollJobStatus);
            handleShowAiMessage(t('generation_completed'));
            fetchTripData();
            setTimeout(() => { fetchCityContexts(); }, 500);
            setTimeout(() => { setIsAiWorking(false); }, 2000);
          } else if (job.status === 'failed') {
            clearInterval(pollJobStatus);
            setError(job.error_message || t('generation_failed'));
            setIsAiWorking(false);
          }
        } catch (pollErr) {
          if (pollCount > 50) { clearInterval(pollJobStatus); setError(t('polling_error')); setIsAiWorking(false); }
        }
      }, 3000);
    } catch (err) {
      setError(t('start_generation_error'));
      setIsAiWorking(false);
    }
  }, [tripId, trip, fetchTripData, setIsAiWorking, handleShowAiMessage, t, fetchCityContexts]);

  // ── Handlers ───────────────────────────────────────────
  const handleAddActivity = (dayNumber) => {
    setSelectedDay(dayNumber);
    setShowAddActivityModal(true);
  };

  const handleEditActivity = (activity) => {
    setEditActivity(activity);
    setShowEditActivityModal(true);
  };

  // Flatten grouped activities back to the raw array shape stored in the offline
  // snapshot, stamping each activity's day/day_date from the group it now sits in
  // so a reload regroups it correctly after an offline edit.
  const flattenGroups = useCallback((groups) => {
    const dateToDay = {};
    computedTripDays.forEach(d => { dateToDay[d.date] = d.dayNumber; });
    const out = [];
    Object.entries(groups).forEach(([dateKey, acts]) => {
      (acts || []).forEach(a => {
        const copy = { ...a };
        if (dateKey !== 'unscheduled') {
          if (dateToDay[dateKey] != null) copy.day = dateToDay[dateKey];
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) copy.day_date = dateKey;
        }
        out.push(copy);
      });
    });
    return out;
  }, [computedTripDays]);

  const persistActivities = useCallback((groups) => {
    saveSnapshot(tripId, { activities: flattenGroups(groups) });
  }, [tripId, flattenGroups]);

  const handleDeleteActivity = async (activity) => {
    if (!activity?.activity_id) return;
    // Optimistic: drop it locally and persist to the offline image first.
    const next = {};
    Object.entries(activitiesByDay).forEach(([k, acts]) => {
      const filtered = acts.filter(a => a.activity_id !== activity.activity_id);
      if (filtered.length) next[k] = filtered;
    });
    setActivitiesByDay(next);
    persistActivities(next);
    const res = await queueOrSend({
      tripId, method: 'DELETE',
      url: `${API_PATH}/activities.php?id=${activity.activity_id}`,
    });
    await refreshPending();
    if (res.sent) fetchTripData({ background: true });
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const activityId = parseInt(draggableId.replace('activity-', ''), 10);
    const targetDay = parseInt(destination.droppableId.replace('day-', ''), 10);
    const sourceDay = parseInt(source.droppableId.replace('day-', ''), 10);
    const dayToDate = {};
    computedTripDays.forEach(d => { dayToDate[d.dayNumber] = d.date; });
    const srcKey = dayToDate[sourceDay];
    const dstKey = dayToDate[targetDay];

    // Optimistic move between day groups.
    const next = {};
    Object.entries(activitiesByDay).forEach(([k, acts]) => { next[k] = [...acts]; });
    const srcArr = next[srcKey] || [];
    const idx = srcArr.findIndex(a => a.activity_id === activityId);
    if (idx >= 0 && dstKey) {
      const [moved] = srcArr.splice(idx, 1);
      if (!next[dstKey]) next[dstKey] = [];
      next[dstKey].splice(destination.index, 0, moved);
      if (srcArr.length === 0 && srcKey !== dstKey) delete next[srcKey];
      else next[srcKey] = srcArr;
      setActivitiesByDay(next);
      persistActivities(next);
    }

    const res = await queueOrSend({
      tripId, method: 'POST',
      url: `${API_PATH}/reorder_activities.php`,
      body: { activity_id: activityId, target_day: targetDay, target_index: destination.index, trip_id: tripId },
    });
    await refreshPending();
    if (res.sent) fetchTripData({ background: true });
  };

  const handlePinActivity = async (activity) => {
    if (!activity?.activity_id) return;
    const nextPinned = !activity.is_pinned;
    const next = {};
    Object.entries(activitiesByDay).forEach(([k, acts]) => {
      next[k] = acts.map(a => a.activity_id === activity.activity_id ? { ...a, is_pinned: nextPinned } : a);
    });
    setActivitiesByDay(next);
    persistActivities(next);
    const res = await queueOrSend({
      tripId, method: 'PUT',
      url: `${API_PATH}/activities.php`,
      body: { activity_id: activity.activity_id, is_pinned: nextPinned },
    });
    await refreshPending();
    if (res.sent) fetchTripData({ background: true });
  };

  const handleOpenTransportationModal = (dayLabel, destination, defaultDate) => {
    setEditingTransportationRecord(null);
    const normalized = (typeof dayLabel === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dayLabel)) ? dayLabel : (defaultDate || null);
    setSelectedDayForTransport(normalized);
    setTransportDefaultDestination(destination || '');
    setTransportDefaultDate(defaultDate || '');
    setShowTransportationModal(true);
  };

  const handleCloseTransportationModal = () => {
    setShowTransportationModal(false);
    setSelectedDayForTransport(null);
    setTransportDefaultDestination('');
    setTransportDefaultDate('');
    setEditingTransportationRecord(null);
    fetchTransportationData();
  };

  const handleCloseAddActivityModal = () => {
    setShowAddActivityModal(false);
    fetchTripData();
  };

  const handleCloseEditActivityModal = () => {
    setShowEditActivityModal(false);
    setEditActivity(null);
    fetchTripData();
  };

  // Update city name for a specific day (offline-capable).
  const handleUpdateCity = useCallback(async (dayNumber, newCity) => {
    // Optimistic local update + persist to the offline image first.
    setComputedTripDays(prev => prev.map(d =>
      d.dayNumber === dayNumber ? { ...d, city: newCity } : d
    ));
    setTrip(prev => {
      if (!prev) return prev;
      const route = Array.isArray(prev.route) ? [...prev.route] : [];
      const idx = route.findIndex(r => r.day === dayNumber);
      if (idx >= 0) {
        route[idx] = { ...route[idx], city: newCity };
      } else {
        route.push({ day: dayNumber, city: newCity });
        route.sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
      }
      const updated = { ...prev, route };
      saveSnapshot(tripId, { trip: updated });
      return updated;
    });
    const res = await queueOrSend({
      tripId, method: 'POST',
      url: `${API_PATH}/trips/update_day_city.php`,
      body: { trip_id: tripId, day: dayNumber, city: newCity },
    });
    await refreshPending();
  }, [tripId, refreshPending]);

  // AI activity suggestions per day
  const handleLaunchDaySuggestions = (day) => {
    const dayIndex = trip?.route?.findIndex(r => r.day === day.dayNumber) ?? (day.dayNumber - 1);
    const routeDay = trip?.route?.[dayIndex] || {};
    setSuggestActivitiesForDay({
      dayNumber: day.dayNumber,
      dayIndex: dayIndex,
      city: routeDay.city || day.city || '',
      country: routeDay.country || day.country || '',
      date: day.date
    });
    setShowInteractiveBuilder(true);
  };
  const handleAIOpenAddActivityModal = (day) => {
    setSelectedDay(day || null);
    setShowAddActivityModal(true);
  };

  const handleAIOpenEditActivityModal = (activityId) => {
    const allActivities = Object.values(activitiesByDay).flat();
    const found = allActivities.find(a => a.activity_id === activityId);
    if (found) { setEditActivity(found); setShowEditActivityModal(true); }
  };

  const handleAIOpenAddFlightModal = (day) => {
    setSelectedDay(day || null);
    setEditActivity(null);
    setShowAddActivityModal(true);
  };

  const fetchViatorData = useCallback(async (activities = null) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const allActivities = activities || Object.values(activitiesByDay).flat();
    const activitiesNeedingViator = allActivities.filter(activity =>
      !activity.viator_price &&
      !activity.viator_link &&
      !viatorFetchedIds.has(activity.activity_id) &&
      activity.location &&
      activity.title &&
      activity.type !== 'flight' &&
      activity.type !== 'lodging' &&
      activity.type !== 'transportation' &&
      (!activity.viator_activity_name || activity.viator_activity_name.toUpperCase() !== 'FREE')
    );
    if (activitiesNeedingViator.length === 0) return;
    setViatorFetchedIds(prev => {
      const updated = new Set(prev);
      activitiesNeedingViator.forEach(act => updated.add(act.activity_id));
      return updated;
    });
    activitiesNeedingViator.forEach(async (activity) => {
      try {
        const response = await fetch(`${API_PATH}/viator_quick_price.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            activity_id: activity.activity_id,
            location: activity.location,
            title: activity.title,
            viator_activity_type: activity.viator_activity_type,
            viator_activity_name: activity.viator_activity_name,
            language: i18n.language
          })
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setActivitiesByDay(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(day => {
                updated[day] = updated[day].map(act =>
                  act.activity_id === activity.activity_id
                    ? { ...act, viator_price: data.viator_price, viator_product_code: data.product_code, viator_match_type: data.match_type }
                    : act
                );
              });
              return updated;
            });
          }
        }
      } catch (_) {}
    });
  }, [activitiesByDay, viatorFetchedIds, i18n.language]);

  // ── Effects ────────────────────────────────────────────
  useEffect(() => {
    if (!tripId) return;
    const userId = localStorage.getItem('pu_id');
    const userParam = userId ? `&user_id=${userId}` : '';
    fetch(`${API_PATH}/expense_reports.php?action=list${userParam}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.json())
      .then(data => {
        const all = [...(data.owned || []), ...(data.shared || [])];
        setLinkedReports(all.filter(r => String(r.trip_id) === String(tripId)));
      })
      .catch(() => {});
  }, [tripId]);
  // Cache-first load: paint the trip's local image instantly, then (if online)
  // replay any queued offline edits and refresh from the server in the background.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await getSnapshot(tripId);
      const hadCache = !!(snap && snap.trip);
      if (!cancelled && hadCache) {
        applySnapshot(snap);
        setLoading(false);
      }
      await refreshPending();
      if (isOnline()) {
        await flushOutbox(tripId);
        if (!cancelled) {
          await fetchTripData({ background: hadCache });
          await refreshPending();
        }
      } else if (!cancelled && !hadCache) {
        // Offline with no cached image — nothing to show.
        setLoading(false);
        setError(t('offline_no_cached_trip'));
      }
    })();
    return () => { cancelled = true; };
  }, [tripId, applySnapshot, fetchTripData, refreshPending, t]);

  // React to connectivity changes: update the chip and, on reconnect, flush the
  // outbox then refresh — all asynchronously.
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const unsub = onReconnect(async () => {
      await flushOutbox(tripId);
      await refreshPending();
      fetchTripData({ background: true });
    });
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      unsub();
    };
  }, [tripId, fetchTripData, refreshPending]);
  useEffect(() => { if (trip && !loading) fetchTransportationData(); }, [trip, loading, fetchTransportationData]);
  useEffect(() => { if (Object.keys(activitiesByDay).length > 0 && !loading) fetchCityContexts(); }, [activitiesByDay, loading, tripId]);
  useEffect(() => { if (Object.keys(activitiesByDay).length > 0 && !loading && viatorFetchedIds.size === 0) fetchViatorData(); }, [activitiesByDay, loading, fetchViatorData, viatorFetchedIds.size]);
  useEffect(() => { initialGenerationStarted.current = false; }, [tripId]);
  useEffect(() => { flightCheckRunRef.current = false; setFlightNotices([]); }, [tripId]);

  // On page load, ask the backend to check the live status of any flight
  // departing within 48h. Verified route changes are auto-applied server-side;
  // route mismatches come back as warnings. Runs once per trip load.
  useEffect(() => {
    if (loading || flightCheckRunRef.current) return;
    if (Object.keys(activitiesByDay).length === 0) return;
    flightCheckRunRef.current = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_PATH}/check_flight_changes.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ trip_id: tripId }),
        });
        const data = await res.json();
        if (!res.ok || !data.success || !Array.isArray(data.changes) || data.changes.length === 0) return;
        setFlightNotices(data.changes);
        // If any change was actually applied, reload activities to show new times.
        if (data.changes.some(c => c.type === 'update')) fetchTripData();
      } catch (e) {
        console.error('Flight change check failed:', e);
      }
    })();
  }, [loading, activitiesByDay, tripId, fetchTripData]);
  useEffect(() => {
    if (location.state?.triggerGeneration && !generationTriggeredFromEdit.current && trip && !loading) {
      generationTriggeredFromEdit.current = true;
      generateItinerary();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, trip, loading, generateItinerary]);

  // ── Derived ────────────────────────────────────────────
  const tripTitle = useMemo(() => {
    if (!trip) return t('trip_default_title');
    const countries = [];
    const seen = new Set();
    Object.keys(activitiesByDay).sort((a, b) => {
      if (a === 'unscheduled') return 1;
      if (b === 'unscheduled') return -1;
      return new Date(a) - new Date(b);
    }).forEach(dayKey => {
      activitiesByDay[dayKey].forEach(act => {
        if (act.location) {
          const parts = act.location.split(',').map(p => p.trim());
          const last = parts[parts.length - 1];
          const isKnown = countriesCities.some(c => c.country === last);
          const country = isKnown ? last : null;
          if (country && !seen.has(country)) { seen.add(country); countries.push(country); }
        }
      });
    });
    return countries.length > 0 ? countries.join(', ') : trip.name || t('trip_default_title');
  }, [trip, activitiesByDay, t]);

  // Map of date (YYYY-MM-DD) → the lodging activity reserved for that night. A
  // lodging activity covers every night from its check-in date (inclusive) up to
  // — but NOT including — its check-out date. Lodging is stored as activities (the
  // lodging table is unused), so derive the reserved nights from structured_data.
  const reservedHotelByDate = useMemo(() => {
    const byDate = {};
    Object.values(activitiesByDay).flat().forEach(act => {
      if (act.type !== 'lodging' && act.type !== 'Lodging') return;
      let sd = act.structured_data;
      if (typeof sd === 'string') { try { sd = JSON.parse(sd); } catch { sd = null; } }
      const checkIn = sd?.check_in_date_time?.slice(0, 10);
      const checkOut = sd?.check_out_date_time?.slice(0, 10);
      if (!checkIn || !checkOut) return;
      // Walk each night from check-in up to (excluding) check-out.
      for (let d = new Date(checkIn + 'T00:00'); d < new Date(checkOut + 'T00:00'); d.setDate(d.getDate() + 1)) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (!byDate[key]) byDate[key] = act;
      }
    });
    return byDate;
  }, [activitiesByDay]);

  // Map of activity_id -> connection info for connecting flights. Built over
  // the full trip in chronological (day, then start_time) order so a flight can
  // connect to the previous flight even across a day boundary (e.g. arrive
  // 23:00, depart 06:00 next day). A flight is a connection when the previous
  // flight's arrival airport is this flight's departure airport, and they are
  // on the same day or within 12h (handled inside getFlightConnection).
  const connectionByActivityId = useMemo(() => {
    const byId = {};
    // Flatten activities across days in order, ignoring the 'unscheduled' bucket.
    const ordered = (tripDays || [])
      .flatMap(day => activitiesByDay[day.date] || []);
    let prevFlight = null;
    ordered.forEach(act => {
      if (!isFlightActivity(act)) return;
      if (prevFlight) {
        const connection = getFlightConnection(prevFlight, act);
        if (connection) byId[act.activity_id] = connection;
      }
      prevFlight = act;
    });
    return byId;
  }, [tripDays, activitiesByDay]);

  // ── Loading state ─────────────────────────────────────
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-80">
        <Loader2 size={36} className="tw-text-accent tw-animate-spin" aria-label="Loading itinerary" />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────
  if (error) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-80">
        <div className="tw-flex tw-items-center tw-gap-3 tw-bg-red-900/20 tw-border tw-border-red-500/30 tw-rounded-2xl tw-px-6 tw-py-4">
          <AlertCircle className="tw-text-red-400 tw-shrink-0" size={20} />
          <span className="tw-text-red-300 tw-text-sm">{error}</span>
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────
  return (
    <>
    <div className={`chat-flip-front${aiAssistantOpen ? ' is-flipped' : ''}`}>
    <div className="tw-p-4 sm:tw-p-6 tw-max-w-3xl tw-mx-auto">

      {/* ── Page header ── */}
      <div className="tw-mb-6">
      <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">

        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          aria-label="Back to trips"
          className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-text-muted-fore tw-hover:tw-bg-surface-3 tw-transition-all tw-duration-200 tw-flex-shrink-0"
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>

        {/* Trip title */}
        <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-muted-fore tw-flex-1 tw-min-w-0 tw-truncate">
          {tripTitle}
        </h1>

        {/* Offline / sync status chip */}
        {(!online || pendingCount > 0) && (
          <div
            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-flex-shrink-0 ${
              online ? 'tw-bg-amber-500/15 tw-text-amber-400' : 'tw-bg-slate-500/20 tw-text-slate-300'
            }`}
            title={online ? t('sync_pending_hint') : t('offline_hint')}
          >
            {online
              ? <RefreshCw size={13} aria-hidden="true" />
              : <CloudOff size={13} aria-hidden="true" />}
            <span>
              {!online
                ? (pendingCount > 0 ? t('offline_pending', { count: pendingCount }) : t('offline_label'))
                : t('sync_syncing', { count: pendingCount })}
            </span>
          </div>
        )}

        {/* Header actions */}
        <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
          <AiImportButton
            onClick={() => setShowAiImport(true)}
            label="Add to this trip from a pasted itinerary using AI"
          />
          <button
            onClick={() => setShowMap(true)}
            aria-label="Show map"
            className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-text-accent-glow tw-hover:tw-bg-accent/10 tw-transition-all tw-duration-200"
          >
            <Map size={18} aria-hidden="true" />
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            aria-label="Share trip"
            className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-text-accent-glow tw-hover:tw-bg-accent/10 tw-transition-all tw-duration-200"
          >
            <Share2 size={18} aria-hidden="true" />
          </button>
          {linkedReports.length > 0 && (
          <button
            onClick={() => navigate(`/expenses/${linkedReports[0].id}`)}
            aria-label="Expenses"
            className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-text-emerald-400 tw-hover:tw-bg-emerald-500/10 tw-transition-all tw-duration-200"
          >
            <DollarSign size={18} aria-hidden="true" />
          </button>
          )}
          <button
            onClick={() => navigate(`/trip/${tripId}/memories`)}
            aria-label="Memories"
            className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-text-pink-400 tw-hover:tw-bg-pink-500/10 tw-transition-all tw-duration-200"
          >
            <Camera size={18} aria-hidden="true" />
          </button>
          <button
            onClick={() => setShowCurrencyConverter(true)}
            aria-label="Currency converter"
            className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-text-yellow-400 tw-hover:tw-bg-yellow-500/10 tw-transition-all tw-duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 1v22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/><path d="m3 9 3-3-3-3"/><path d="m21 15-3 3 3 3"/></svg>
          </button>
        </div>
      </div>

      {/* Set dates button — shown below header for flexible trips */}
      {isTripFlexible && (
        <div className="tw-mt-2 tw-pl-1">
          <button
            onClick={handleOpenDatePicker}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-text-accent-glow tw-border tw-border-accent/40 tw-hover:tw-bg-accent/10 tw-transition-all tw-duration-200"
          >
            <Calendar size={13} aria-hidden="true" />
            {t('set_trip_dates_btn')}
          </button>
        </div>
      )}

      {/* Date + duration sub-line */}
      {(trip.start_date || trip.duration_days) && (
        <div className="tw-flex tw-items-center tw-gap-4 tw-mt-1.5 tw-pl-1">
          {(trip.start_date && trip.end_date) && (
            <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-muted-fore">
              <Calendar size={13} className="tw-text-accent" aria-hidden="true" />
              {trip.start_date} → {trip.end_date}
            </span>
          )}
          {trip.duration_days && (
            <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-muted-fore">
              <Clock size={13} className="tw-text-accent" aria-hidden="true" />
              {trip.duration_days} days
            </span>
          )}
        </div>
      )}
      </div>

      {/* ── Duration explanation banner ── */}
      {trip.duration_explanation && (
        <div className="tw-mb-4 tw-px-4 tw-py-3 tw-rounded-2xl tw-bg-amber-900/20 tw-border tw-border-amber-500/20 tw-flex tw-items-start tw-gap-2">
          <Info size={15} className="tw-text-amber-400 tw-shrink-0 tw-mt-0.5" aria-hidden="true" />
          <p className="tw-text-xs tw-text-amber-200/80 tw-leading-relaxed">
            <span className="tw-font-semibold tw-text-amber-300">Why {trip.duration_days} days:</span>{' '}
            {trip.duration_explanation}
          </p>
        </div>
      )}

      {/* ── Set Dates dialog ── */}
      {showDatePicker && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60" onClick={handleCloseDatePicker}>
          <div className="tw-bg-surface-2 tw-rounded-2xl tw-p-6 tw-w-full tw-max-w-sm tw-mx-4 tw-shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="tw-text-lg tw-font-bold tw-text-muted-fore tw-mb-4">{t('set_trip_dates_dialog_title')}</h2>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div>
                <label className="tw-block tw-text-sm tw-text-muted tw-mb-1">{t('start_date_label')}</label>
                <input
                  type="date"
                  value={tempStartDate}
                  onChange={e => setTempStartDate(e.target.value)}
                  className="tw-w-full tw-rounded-xl tw-bg-surface-3 tw-border tw-border-white/10 tw-px-3 tw-py-2 tw-text-muted-fore tw-text-sm focus:tw-outline-none focus:tw-border-accent"
                />
              </div>
              <div>
                <label className="tw-block tw-text-sm tw-text-muted tw-mb-1">{t('duration_days_label')}</label>
                <input
                  type="number"
                  min="1"
                  value={tempDuration}
                  onChange={e => setTempDuration(e.target.value)}
                  className="tw-w-full tw-rounded-xl tw-bg-surface-3 tw-border tw-border-white/10 tw-px-3 tw-py-2 tw-text-muted-fore tw-text-sm focus:tw-outline-none focus:tw-border-accent"
                />
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-6">
              <button onClick={handleCloseDatePicker} className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-text-muted tw-hover:tw-bg-surface-3 tw-transition-colors">
                {t('cancel_btn')}
              </button>
              <button onClick={handleSaveTripDates} className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent tw-text-white tw-hover:tw-bg-accent/80 tw-transition-colors">
                {t('save_dates_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI working indicator ── */}
      {isAiWorking && (
        <div
          className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-2xl tw-bg-emerald-900/25 tw-border tw-border-emerald-500/30"
          role="status"
          aria-live="polite"
        >
          <Loader2 size={15} className="tw-text-emerald-400 tw-animate-spin" aria-hidden="true" />
          <span className="tw-text-sm tw-font-semibold tw-text-emerald-300">AI agent is working…</span>
          <div className="tw-flex tw-gap-1 tw-ml-1">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-400 tw-animate-pulse"
                style={{ animationDelay: `${i * 200}ms` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Flight change / verification notices (checked on load) ── */}
      {flightNotices.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-3">
          {flightNotices.map((n, i) => {
            const isMismatch = n.type === 'mismatch';
            const tone = isMismatch
              ? 'tw-border-amber-400 tw-bg-amber-400/10'
              : 'tw-border-sky-400 tw-bg-sky-400/10';
            const iconColor = isMismatch ? 'tw-text-amber-400' : 'tw-text-sky-400';
            return (
              <div key={`${n.activity_id}-${i}`} className={`tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-2.5 tw-rounded-lg tw-border-l-4 ${tone}`}>
                {isMismatch
                  ? <AlertCircle size={18} className={`${iconColor} tw-mt-0.5 tw-shrink-0`} aria-hidden="true" />
                  : <Plane size={18} className={`${iconColor} tw-mt-0.5 tw-shrink-0 tw-rotate-45`} aria-hidden="true" />}
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">
                    {isMismatch ? '⚠ ' : ''}{n.summary}
                  </p>
                  {Array.isArray(n.details) && n.details.map((d, j) => (
                    <p key={j} className="tw-text-xs tw-text-muted tw-mt-0.5">{d}</p>
                  ))}
                </div>
                <button
                  onClick={() => setFlightNotices(prev => prev.filter((_, idx) => idx !== i))}
                  aria-label="Dismiss"
                  className="tw-text-muted tw-hover:tw-text-muted-fore tw-text-lg tw-leading-none tw-px-1 tw-shrink-0"
                >×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Day cards ── */}
      <div className="tw-flex tw-flex-col tw-gap-3">
        <DragDropContext onDragEnd={handleDragEnd}>
        {tripDays.map((day) => (
          <DayCard
            key={day.date}
            day={day}
            activities={activitiesByDay[day.date] ?? []}
            connectionByActivityId={connectionByActivityId}
            hotelReservation={reservedHotelByDate[day.date] || null}
            onAdd={handleAddActivity}
            onAiSuggest={(d) => {
              setAiContext({ type: 'day', id: d.dayNumber, activity: null });
              setAiAssistantOpen(true);
            }}
            onSuggestActivities={handleLaunchDaySuggestions}
            onEditActivity={handleEditActivity}
            onDeleteActivity={handleDeleteActivity}
            onPinActivity={handlePinActivity}
            onShowDetails={(activity) => { setSelectedActivity(activity); setShowDetailsModal(true); }}
            onUpdateCity={handleUpdateCity}
          />
        ))}
        </DragDropContext>

        {/* Empty state when no days */}
        {tripDays.length === 0 && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-text-center">
            <span className="tw-p-6 tw-rounded-full tw-bg-accent/10 tw-mb-4">
              <Calendar size={48} className="tw-text-accent" aria-hidden="true" />
            </span>
            <h3 className="tw-text-lg tw-font-semibold tw-text-muted-fore tw-mb-1">No itinerary yet</h3>
            <p className="tw-text-sm tw-text-muted tw-max-w-xs">
              Ask AI to build your day-by-day plan, or add days manually.
            </p>
          </div>
        )}
      </div>

      {/* ── Floating Add Activity button ── */}
      {/* Add Activity FAB */}
      <button
        onClick={() => handleAddActivity(tripDays[0]?.dayNumber ?? 1)}
        aria-label="Add activity"
        className="tw-fixed tw-bottom-6 tw-right-6 tw-flex tw-items-center tw-gap-2 tw-bg-accent tw-hover:tw-bg-accent-600 tw-text-white tw-font-semibold tw-px-5 tw-py-3 tw-rounded-2xl tw-shadow-xl tw-shadow-accent/30 tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.04]"
      >
        <Plus size={18} aria-hidden="true" />
        <span className="tw-hidden sm:tw-inline">Add Activity</span>
      </button>

      {/* AI Assistant FAB — floats above the Add Activity button */}
      <button
        onClick={() => {
          setAiContext({ type: 'general', id: null, activity: null });
          setAiAssistantOpen(true);
        }}
        aria-label="Open AI assistant"
        className="tw-fixed tw-bottom-[4.5rem] tw-right-7 tw-w-11 tw-h-11 tw-rounded-full tw-bg-violet-600 tw-hover:tw-bg-violet-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-shadow-violet-500/40 tw-transition-all tw-duration-200 tw-hover:tw-scale-110"
      >
        <AIIcon sx={{ fontSize: 22 }} />
      </button>
    </div>
    </div>{/* /chat-flip-front */}
    {showAddActivityModal && (
      <AddActivityModal
        open={showAddActivityModal}
        handleClose={handleCloseAddActivityModal}
        tripId={tripId}
        trip={trip}
        tripDays={tripDays}
        selectedDay={selectedDay}
        showLoggingOption={true}
      />
    )}
    {showEditActivityModal && (
      <AddActivityModal
        open={showEditActivityModal}
        handleClose={handleCloseEditActivityModal}
        tripId={tripId}
        trip={trip}
        tripDays={tripDays}
        activity={editActivity}
        isEdit={true}
      />
    )}
    {showTransportationModal && (
      <TransportationFormModal
        open={showTransportationModal}
        onClose={handleCloseTransportationModal}
        tripId={tripId}
        dayLabel={selectedDayForTransport}
        defaultDestination={transportDefaultDestination}
        defaultDate={transportDefaultDate}
        editRecord={editingTransportationRecord}
      />
    )}
    {showMap && (
      <TripMap
        open={showMap}
        onClose={() => setShowMap(false)}
        tripId={tripId}
        trip={trip}
        activitiesByDay={activitiesByDay}
        tripDays={tripDays}
      />
    )}
    {showShareModal && (
      <ShareTripModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        trip={trip}
        user={user}
      />
    )}
    <AiItineraryImportModal
      open={showAiImport}
      tripId={tripId}
      existingTripName={trip?.name || tripTitle}
      onClose={() => setShowAiImport(false)}
      onComplete={() => {
        setShowAiImport(false);
        fetchTripData();
      }}
    />
    <CurrencyConverterModal
      open={showCurrencyConverter}
      onClose={() => setShowCurrencyConverter(false)}
      tripId={tripId}
      user={user}
    />
    {showDetailsModal && selectedActivity && (
      <ActivityDetailsModal
        open={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setSelectedActivity(null); }}
        activity={Object.values(activitiesByDay).flat().find(a => a.activity_id === selectedActivity?.activity_id) || selectedActivity}
        trip={trip}
        user={user}
        onEdit={(activity) => { setShowDetailsModal(false); setSelectedActivity(null); handleEditActivity(activity); }}
        onDelete={(activity) => { setShowDetailsModal(false); setSelectedActivity(null); handleDeleteActivity(activity); }}
        onPin={(activity) => { handlePinActivity(activity); }}
        onActivityUpdated={fetchTripData}
      />
    )}
    {showInteractiveBuilder && suggestActivitiesForDay && (
      <div
        className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-start tw-justify-center tw-bg-black/60 tw-backdrop-blur-sm tw-overflow-y-auto tw-p-4"
        onClick={(e) => { if (e.target === e.currentTarget) { setShowInteractiveBuilder(false); setSuggestActivitiesForDay(null); } }}
        role="dialog"
        aria-modal="true"
      >
        <div className="tw-relative tw-w-full tw-max-w-2xl tw-my-8" onClick={e => e.stopPropagation()}>
          <InteractiveDailyActivityBuilder
            tripId={tripId}
            tripData={trip}
            initialDayIndex={suggestActivitiesForDay.dayIndex}
            singleDayMode={true}
            autoLoad={true}
            existingActivities={activitiesByDay}
            onComplete={() => { fetchTripData(); setShowInteractiveBuilder(false); setSuggestActivitiesForDay(null); }}
            onCancel={() => { setShowInteractiveBuilder(false); setSuggestActivitiesForDay(null); }}
            onClose={() => { setShowInteractiveBuilder(false); setSuggestActivitiesForDay(null); }}
            setIsAiWorking={setIsAiWorking}
            handleShowAiMessage={handleShowAiMessage}
          />
        </div>
      </div>
    )}
    <div className={`chat-flip-back${aiAssistantOpen ? ' is-flipped' : ''}`} aria-hidden={!aiAssistantOpen}>
      {aiAssistantOpen && (
        <AIAssistant
          open={aiAssistantOpen}
          onClose={() => setAiAssistantOpen(false)}
          tripId={tripId}
          tripData={trip}
          contextType={aiContext.type}
          contextId={aiContext.id}
          contextActivity={aiContext.activity}
          onOpenAddFlightModal={handleAIOpenAddFlightModal}
          onOpenAddActivityModal={handleAIOpenAddActivityModal}
          onOpenEditActivityModal={handleAIOpenEditActivityModal}
        />
      )}
    </div>
    </>
  );
};

export default ItineraryPageV2;
